// Gráficas del dashboard. Una sola serie y un solo tono por gráfica (la magnitud la lleva la altura o el largo,
// no el color); el texto usa los colores de texto, nunca el de la serie; cada marca tiene su valor al pasar el
// cursor o al enfocarla con el teclado, y una tabla equivalente para lectores de pantalla.
import { useState } from 'react';
import { Icon } from '../components/Icon';

export interface DayPoint {
  day: string;
  total: number;
  count: number;
}

/** Alto de cada barra (0–1) respecto al máximo de la serie; una serie en ceros queda plana. */
export function barHeights(values: number[]): number[] {
  const max = Math.max(0, ...values);
  return values.map((v) => (max > 0 ? Math.max(0, v) / max : 0));
}

/** Variación porcentual frente al periodo anterior; null cuando no hay con qué comparar. */
export function trendPercent(current: number, previous: number): number | null {
  if (previous <= 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

/** Tendencia: flecha + porcentaje + contra qué se compara (nunca solo color). */
export function Trend({ current, previous, label, id }: { current: number; previous: number; label: string; id?: string }) {
  const pct = trendPercent(current, previous);
  if (pct === null) return null;
  const dir = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';
  return (
    <span className={`trend ${dir}`} id={id} data-trend={dir}>
      <Icon name={dir === 'up' ? 'arrowUp' : dir === 'down' ? 'arrowDown' : 'minus'} size={12} />
      {`${pct > 0 ? '+' : ''}${pct}% ${label}`}
    </span>
  );
}

/** Ventas por día: barras delgadas con extremo redondeado, separadas por el color de la superficie. */
export function SalesBars({ id, points, formatValue, formatDay, tableCaption, emptyLabel, dayLabel, valueLabel }: {
  id: string;
  points: DayPoint[];
  formatValue: (n: number) => string;
  formatDay: (day: string) => string;
  tableCaption: string;
  /** Texto cuando la serie está en ceros (una gráfica vacía sin explicación parece un error). */
  emptyLabel: string;
  dayLabel: string;
  valueLabel: string;
}) {
  const [active, setActive] = useState<number | null>(null);
  const heights = barHeights(points.map((p) => p.total));
  const shown = active ?? points.length - 1;
  const current = points[shown];
  return (
    <figure className="chart" id={id}>
      <div className="chartreadout" aria-live="polite">
        <b>{current ? formatValue(current.total) : ''}</b>
        <span>{current ? formatDay(current.day) : ''}</span>
      </div>
      {points.every((p) => p.total === 0) ? <p className="chartempty">{emptyLabel}</p> : null}
      <div className="salesbars" role="img" aria-label={tableCaption} onMouseLeave={() => setActive(null)}>
        {points.map((p, i) => (
          <button
            type="button" key={p.day} className={i === shown ? 'bar active' : 'bar'} data-day={p.day} data-total={p.total}
            title={`${formatDay(p.day)} · ${formatValue(p.total)}`} aria-label={`${formatDay(p.day)}: ${formatValue(p.total)}`}
            onMouseEnter={() => setActive(i)} onFocus={() => setActive(i)} onBlur={() => setActive(null)}
          >
            <span style={{ height: `${Math.max(heights[i]! * 100, p.total > 0 ? 4 : 0)}%` }} />
          </button>
        ))}
      </div>
      <div className="chartaxis">
        <span>{points[0] ? formatDay(points[0].day) : ''}</span>
        <span>{points[points.length - 1] ? formatDay(points[points.length - 1]!.day) : ''}</span>
      </div>
      <table className="sr-only">
        <caption>{tableCaption}</caption>
        <thead>
          <tr>
            <th>{dayLabel}</th>
            <th>{valueLabel}</th>
          </tr>
        </thead>
        <tbody>
          {points.map((p) => (
            <tr key={p.day}>
              <td>{formatDay(p.day)}</td>
              <td>{formatValue(p.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

/** Existencias por ubicación: barras horizontales con el nombre y el valor escritos (rótulo directo). */
export function LocationBars({ id, rows, formatValue }: { id: string; rows: { code: string; name: string; units: number }[]; formatValue: (n: number) => string }) {
  const widths = barHeights(rows.map((r) => r.units));
  return (
    <div className="locbars" id={id}>
      {rows.map((r, i) => (
        <div className="locbar" key={r.code} data-location={r.code} title={`${r.name} · ${formatValue(r.units)}`}>
          <span className="locname">{r.name}</span>
          <span className="loctrack">
            <span className="locfill" style={{ width: `${Math.max(widths[i]! * 100, r.units > 0 ? 1.5 : 0)}%` }} />
          </span>
          <span className="locvalue">{formatValue(r.units)}</span>
        </div>
      ))}
    </div>
  );
}
